/**
 * advance-ai-diplomacy.ts — pure helpers powering the AI's diplomacy
 * decisions (v3M21.5).
 *
 * This module only exports data + scoring; the actual `setDiplomaticFocus`
 * / `invokeAnnex` / etc. dispatch lands in `advance-ai.ts` (v3M21.5.3 +
 * .4). Splitting the helpers out keeps the scoring formula testable in
 * isolation and lets the dispatcher branch on a single readable score
 * rather than re-deriving it from the fields each tick.
 *
 * The synthetic neutral empire (`EMPIRE_NEUTRAL_ID`) is filtered upstream
 * by the iteration-site sweep landed in v3M21.1.6 — it never reaches
 * these helpers.
 */

import type { Empire, GameState, Planet } from '../state/types/index';
import { EMPIRE_NEUTRAL_ID } from '../state/types/index';

// ---------------------------------------------------------------------------
// Tuning constants
// ---------------------------------------------------------------------------

/**
 * Per-Pop weight in the score. A 50-Pop neutral contributes
 * `50 × POP_VALUE_WEIGHT` to its score. Default 1.0 — Pop is the
 * headline expected-value driver since annexing a populated neutral
 * adds population, structures, and economy to the empire in one move.
 */
export const POP_VALUE_WEIGHT = 1.0;

/**
 * Multiplier on the per-tick Trade rate (Credits + 2 × Minerals
 * weighted) when scoring. Higher = AI values a Trade arrangement
 * more, even if Annex is unreachable. Default 50 — equates one
 * Credits/tick to ~50 score points, comparable to a 50-Pop annex
 * candidate.
 */
export const TRADE_INCOME_WEIGHT = 50.0;

/**
 * Sunk-cost momentum weight. An empire that's already invested 600
 * Influence (vassal threshold) on a planet should be reluctant to
 * abandon it. The score adds `(currentInfluence / annexThreshold) ×
 * MOMENTUM_WEIGHT`. Default 100 — at full Annex threshold the
 * momentum bonus is 100, a nudge but not overwhelming.
 */
export const MOMENTUM_WEIGHT = 100.0;

/**
 * Penalty for planets currently federated to a rival empire. The lock
 * caps non-federated empires' pools at federationThreshold, which is
 * below Vassal (600) and Annex (800), so those outcomes are
 * unreachable. Trade (200) is still allowed but worth less than
 * unfederated alternatives. Default 200 — strong de-prioritisation
 * without zeroing the planet entirely.
 */
export const FEDERATION_LOCK_PENALTY = 200.0;

/**
 * Penalty for planets currently vassalised to a rival empire. The AI
 * can still Vassal-flip if it reaches `vassalThreshold`, but there's
 * friction (the rival's tribute is currently flowing to them, not us).
 * Default 50 — mild de-prioritisation; Vassal flip is a real option.
 */
export const VASSAL_RIVAL_PENALTY = 50.0;

/**
 * Aggression-floor below which the AI ignores diplomacy entirely.
 * Default 0.1 — Aggressive (0.2 base) × RACE-BB warrior penalty
 * (0.7 multiplier) = 0.14, just above the floor; below this the AI
 * never sets focus + never spends Diplomatic resource.
 */
export const PICK_EPSILON = 0.1;

/**
 * Effective-aggression cutoff at or above which the AI saves Influence
 * for Annex rather than invoking the best currently-available outcome.
 * Default 0.5 — Balanced base (0.5) × 1.0× = 0.5 sits exactly at the
 * cutoff (deterministic tie-breaker via `>=`); Economist (0.6) ×
 * RACE-AA (1.5×) = 0.9 is comfortably aggressive; Aggressive (0.2) ×
 * RACE-BB (0.7×) = 0.14 is comfortably conservative.
 */
export const AGGRESSIVE_THRESHOLD = 0.5;

/**
 * Number of ticks of "patience" an aggressive AI gives itself when
 * deciding whether Annex is reachable. If `currentPool + tickIncome ×
 * AGGRESSIVE_PATIENCE_TICKS >= annexThreshold`, the AI waits; else it
 * invokes whatever's currently unlocked. Default 1000 ticks (~100s
 * of game time at 10 Hz) — long enough that a steady single-Embassy
 * income (~1/tick) covers the 400→800 Annex gap once Federation has
 * landed, but short enough that an Embassy-less AI doesn't deadlock
 * waiting for income that will never materialise.
 */
export const AGGRESSIVE_PATIENCE_TICKS = 1000;

/**
 * The four invocable diplomatic outcomes plus a "wait" sentinel. The
 * AI dispatcher branches on this to pick which Command to issue (or
 * to do nothing this tick).
 */
export type DiplomaticDecision =
  | 'wait'
  | 'trade'
  | 'federation'
  | 'vassal'
  | 'annex';

// ---------------------------------------------------------------------------
// Visibility
// ---------------------------------------------------------------------------

/**
 * Walk the empire's `scoutedSystemIds` set, return the neutral
 * planets in those systems (i.e. owned by `EMPIRE_NEUTRAL_ID`). FoW-
 * gated — empires only "see" neutrals in systems they've previously
 * had a ship pass through. Pure: no side effects, no order
 * guarantees beyond `state.planets` Map iteration order.
 */
export function findVisibleNeutrals(
  state: GameState,
  empire: Empire,
): Planet[] {
  const out: Planet[] = [];
  const neutral = state.empires.get(EMPIRE_NEUTRAL_ID);
  if (!neutral) return out;
  for (const pid of neutral.planetIds) {
    const planet = state.planets.get(pid);
    if (!planet) continue;
    if (!empire.scoutedSystemIds.has(planet.systemId)) continue;
    out.push(planet);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Effective aggression
// ---------------------------------------------------------------------------

/**
 * Empire's effective diplomatic aggression =
 * `behaviour.diplomaticAggression × race.diplomaticAggressionMultiplier`,
 * clamped to [0, 2]. Reads from `state.aiBehaviours.behaviours[*]` and
 * `state.races[*]`. Falls back to `1.0` for the multiplier when
 * `state.races` is absent (test fixtures) or when the empire's race
 * isn't in the catalogue (synthetic 'Neutral' race — never reaches
 * here in production thanks to the v3M21.1.6 iteration sweep).
 *
 * The clamp protects against authoring errors that would produce
 * negative or wildly-high values; legitimate values fit comfortably
 * in [0, 1.5].
 */
export function effectiveDiplomaticAggression(
  state: GameState,
  empire: Empire,
): number {
  // v4M8.1 — Empire field rename `aiBehaviour` → `aiArchetype`. The
  // behaviours map on AiBehavioursData is re-keyed to the new archetype
  // IDs (Aggressor / Defender / Expander / Explorer / Diplomat / Researcher).
  const behaviour = state.aiBehaviours.behaviours[empire.aiArchetype];
  const base = behaviour?.diplomaticAggression ?? 0;
  const raceRow = state.races?.find((r) => r.raceId === empire.raceId);
  const multiplier = raceRow?.diplomaticAggressionMultiplier ?? 1.0;
  return Math.max(0, Math.min(2, base * multiplier));
}

// ---------------------------------------------------------------------------
// Per-neutral score
// ---------------------------------------------------------------------------

/**
 * Per-neutral expected-value score for AI focus selection. Higher =
 * more attractive target. Pure — same inputs always produce the same
 * output. Components:
 *
 *   popScore                — `planet.currentPopulation × POP_VALUE_WEIGHT`
 *   tradeIncomeScore        — `(creditsPerTick + 2 × mineralsPerTick) × TRADE_INCOME_WEIGHT`
 *   momentumScore           — `(currentEmpireInfluence / annexThreshold) × MOMENTUM_WEIGHT`
 *   - federationLockPenalty — applied if a rival is federated
 *   - vassalRivalPenalty    — applied if a rival is suzerain
 *
 * All weights are exported constants — retunes are surgical, no
 * formula refactor needed during v3M21.7's playtest pass.
 */
export function scoreNeutral(
  state: GameState,
  empire: Empire,
  planet: Planet,
): number {
  const popScore = planet.currentPopulation * POP_VALUE_WEIGHT;

  const trade = state.balanceData.diplomacy.trade;
  const tradeIncomeScore =
    (trade.creditsPerTick + 2 * trade.mineralsPerTick) * TRADE_INCOME_WEIGHT;

  const annexThreshold = state.balanceData.diplomacy.thresholds.annex;
  const currentInfluence = planet.influence.get(empire.id) ?? 0;
  const momentumScore = (currentInfluence / annexThreshold) * MOMENTUM_WEIGHT;

  let federationLockPenalty = 0;
  if (planet.federatedWith !== null && planet.federatedWith !== empire.id) {
    federationLockPenalty = FEDERATION_LOCK_PENALTY;
  }

  let vassalRivalPenalty = 0;
  if (planet.vassalOf !== null && planet.vassalOf !== empire.id) {
    vassalRivalPenalty = VASSAL_RIVAL_PENALTY;
  }

  return (
    popScore +
    tradeIncomeScore +
    momentumScore -
    federationLockPenalty -
    vassalRivalPenalty
  );
}

/**
 * v3M21.5.3 — pick the highest-scoring visible neutral as the empire's
 * diplomatic-focus target, or `null` if there are no eligible neutrals
 * in scouted systems. Pure — no side effects; the caller in
 * `advance-ai.ts` is responsible for dispatching `setDiplomaticFocus`.
 *
 * Tie-breaking is iteration-order-stable (uses strict `>`, so the
 * first-seen of equally-scored neutrals wins). With a fixed seed the
 * pick is therefore deterministic.
 */
export function chooseDiplomaticTarget(
  state: GameState,
  empire: Empire,
): Planet | null {
  const candidates = findVisibleNeutrals(state, empire);
  let best: Planet | null = null;
  let bestScore = -Infinity;
  for (const planet of candidates) {
    const s = scoreNeutral(state, empire, planet);
    if (s > bestScore) {
      bestScore = s;
      best = planet;
    }
  }
  return best;
}

/**
 * v3M21.5.4 — decide which diplomatic outcome (if any) the empire
 * should invoke on its focus target this tick. Pure: takes only state
 * + empire + the focus planet, returns the decision sentinel.
 *
 * Decision tree:
 *   1. Pool ≥ Annex threshold → 'annex' (always best when reachable).
 *   2. Aggressive AI (effective aggression ≥ AGGRESSIVE_THRESHOLD):
 *      compute Annex reachability over `AGGRESSIVE_PATIENCE_TICKS`
 *      using the empire's per-tick Diplomatic income. If Annex is
 *      reachable in that window, return 'wait' — keep saving.
 *      Otherwise fall through to the priority cascade below.
 *   3. Conservative AI (or aggressive AI past patience): take the
 *      highest-tier unlocked outcome that doesn't conflict with
 *      prior invocations or other-empire locks.
 *      Vassal > Federation > Trade in priority order.
 *      - Vassal requires the planet to NOT currently be federated
 *        (Federation supersedes Vassal per `DIPLOMACY.md §6.4`).
 *      - Federation requires the planet to NOT currently be
 *        federated (idempotent self-re-invoke is a no-op anyway).
 *      - Trade is non-exclusive; only re-invoke if the empire isn't
 *        already in `tradingWith[]`.
 *   4. If none of the above unlock, return 'wait'.
 *
 * Income source for the patience calculation: the empire's per-tick
 * Embassy output (matches the v3M21.2 routing path — when focus is
 * set, all of that income flows into the pool).
 */
export function decideOutcome(
  state: GameState,
  empire: Empire,
  planet: Planet,
  empireDiplomaticIncomePerTick: number,
): DiplomaticDecision {
  const pool = planet.influence.get(empire.id) ?? 0;
  const t = state.balanceData.diplomacy.thresholds;

  if (pool >= t.annex) return 'annex';

  const aggression = effectiveDiplomaticAggression(state, empire);
  if (aggression >= AGGRESSIVE_THRESHOLD) {
    const reachable =
      pool + empireDiplomaticIncomePerTick * AGGRESSIVE_PATIENCE_TICKS;
    if (reachable >= t.annex) return 'wait';
    // Annex unreachable in window — fall through and take what's
    // available right now.
  }

  if (pool >= t.vassal && planet.federatedWith === null) {
    return 'vassal';
  }
  if (pool >= t.federation && planet.federatedWith === null) {
    return 'federation';
  }
  if (pool >= t.trade && !planet.tradingWith.includes(empire.id)) {
    return 'trade';
  }
  return 'wait';
}
